import { Env, Urls } from 'src/appConstants';
import { SentryPayoutAccountStep } from 'src/modules/sentry-taxonomy';
import {
	resetSentryPayoutAccountState,
	setSentryPayoutAccountState,
} from 'src/modules/sentry-payout-account-state';

type ScenarioName =
	| 'auth_login_network'
	| 'auth_login_500'
	| 'auth_oauth_network'
	| 'auth_logout_network'
	| 'auth_recovery_500'
	| 'auth_signup_500'
	| 'checkout_open_sell_network'
	| 'checkout_open_sell_500'
	| 'checkout_edit_sell_500'
	| 'checkout_coupon_500'
	| 'checkout_otp_500'
	| 'checkout_add_card_500'
	| 'checkout_validate_card_500'
	| 'checkout_buy_500'
	| 'checkout_pay_500'
	| 'checkout_pix_status_500'
	| 'event_creation_load_categories_500'
	| 'event_creation_create_draft_network'
	| 'event_creation_create_draft_500'
	| 'event_creation_load_draft_500'
	| 'event_creation_create_edit_draft_500'
	| 'event_creation_load_sessions_500'
	| 'event_creation_load_tickets_500'
	| 'event_creation_create_place_500'
	| 'event_creation_save_ticket_500'
	| 'event_creation_sort_tickets_500'
	| 'event_creation_upload_banner_500'
	| 'event_creation_update_tax_500'
	| 'event_creation_publish_500'
	| 'public_event_load_500'
	| 'public_event_ticket_types_500'
	| 'public_event_sell_open_500'
	| 'public_event_contact_500'
	| 'public_event_complaint_500'
	| 'event_management_dashboard_500'
	| 'event_management_sales_500'
	| 'event_management_checkin_500'
	| 'event_management_coupons_500'
	| 'event_management_guests_500'
	| 'payout_validate_phone_500'
	| 'payout_load_banks_500'
	| 'payout_save_recipient_500'
	| 'payout_register_bank_account_network'
	| 'payout_register_bank_account_500'
	| 'payout_check_status_500';

type ErrorKind = 'network' | 'http_500';

type ScenarioDefinition = {
	name: ScenarioName;
	baseURL?: string;
	method: 'GET' | 'POST' | 'PUT';
	url: string;
	errorKind: ErrorKind;
	data?: unknown;
	payoutStep?: SentryPayoutAccountStep;
};

type ScenarioResult = {
	scenario: ScenarioName;
	expectedQuery: string;
};

type SentryTestScenarios = {
	loginNetwork: () => Promise<ScenarioResult>;
	login500: () => Promise<ScenarioResult>;
	oauthNetwork: () => Promise<ScenarioResult>;
	logoutNetwork: () => Promise<ScenarioResult>;
	recovery500: () => Promise<ScenarioResult>;
	signup500: () => Promise<ScenarioResult>;
	all: () => Promise<ScenarioResult[]>;
};

type SentryCheckoutTestScenarios = {
	openSellNetwork: () => Promise<ScenarioResult>;
	openSell500: () => Promise<ScenarioResult>;
	editSell500: () => Promise<ScenarioResult>;
	coupon500: () => Promise<ScenarioResult>;
	otp500: () => Promise<ScenarioResult>;
	addCard500: () => Promise<ScenarioResult>;
	validateCard500: () => Promise<ScenarioResult>;
	buy500: () => Promise<ScenarioResult>;
	pay500: () => Promise<ScenarioResult>;
	pixStatus500: () => Promise<ScenarioResult>;
	all: () => Promise<ScenarioResult[]>;
};

type SentryEventCreationTestScenarios = {
	loadCategories500: () => Promise<ScenarioResult>;
	createDraftNetwork: () => Promise<ScenarioResult>;
	createDraft500: () => Promise<ScenarioResult>;
	loadDraft500: () => Promise<ScenarioResult>;
	createEditDraft500: () => Promise<ScenarioResult>;
	loadSessions500: () => Promise<ScenarioResult>;
	loadTickets500: () => Promise<ScenarioResult>;
	createPlace500: () => Promise<ScenarioResult>;
	saveTicket500: () => Promise<ScenarioResult>;
	sortTickets500: () => Promise<ScenarioResult>;
	uploadBanner500: () => Promise<ScenarioResult>;
	updateTax500: () => Promise<ScenarioResult>;
	publish500: () => Promise<ScenarioResult>;
	all: () => Promise<ScenarioResult[]>;
};

type SentryPayoutAccountTestScenarios = {
	validatePhone500: () => Promise<ScenarioResult>;
	loadBanks500: () => Promise<ScenarioResult>;
	saveRecipient500: () => Promise<ScenarioResult>;
	registerBankAccountNetwork: () => Promise<ScenarioResult>;
	registerBankAccount500: () => Promise<ScenarioResult>;
	checkStatus500: () => Promise<ScenarioResult>;
	all: () => Promise<ScenarioResult[]>;
};

type SentryPublicEventTestScenarios = {
	loadEvent500: () => Promise<ScenarioResult>;
	loadTicketTypes500: () => Promise<ScenarioResult>;
	openSell500: () => Promise<ScenarioResult>;
	contact500: () => Promise<ScenarioResult>;
	complaint500: () => Promise<ScenarioResult>;
	all: () => Promise<ScenarioResult[]>;
};

type SentryEventManagementTestScenarios = {
	dashboard500: () => Promise<ScenarioResult>;
	sales500: () => Promise<ScenarioResult>;
	checkin500: () => Promise<ScenarioResult>;
	coupons500: () => Promise<ScenarioResult>;
	guests500: () => Promise<ScenarioResult>;
	all: () => Promise<ScenarioResult[]>;
};

declare global {
	interface Window {
		__SENTRY_AUTH_TESTS__?: SentryTestScenarios;
		__SENTRY_CHECKOUT_TESTS__?: SentryCheckoutTestScenarios;
		__SENTRY_EVENT_CREATION_TESTS__?: SentryEventCreationTestScenarios;
		__SENTRY_PAYOUT_ACCOUNT_TESTS__?: SentryPayoutAccountTestScenarios;
		__SENTRY_PUBLIC_EVENT_TESTS__?: SentryPublicEventTestScenarios;
		__SENTRY_EVENT_MANAGEMENT_TESTS__?: SentryEventManagementTestScenarios;
	}
}

const authDefinitions: Record<
	keyof Omit<SentryTestScenarios, 'all'>,
	ScenarioDefinition
> = {
	loginNetwork: {
		name: 'auth_login_network',
		baseURL: Urls.AUTH_API,
		method: 'POST',
		url: 'signin',
		errorKind: 'network',
		data: { email: 'synthetic', password: 'synthetic' },
	},
	login500: {
		name: 'auth_login_500',
		baseURL: Urls.AUTH_API,
		method: 'POST',
		url: 'signin',
		errorKind: 'http_500',
		data: { email: 'synthetic', password: 'synthetic' },
	},
	oauthNetwork: {
		name: 'auth_oauth_network',
		baseURL: Urls.AUTH_API,
		method: 'GET',
		url: 'oauth-signin',
		errorKind: 'network',
	},
	logoutNetwork: {
		name: 'auth_logout_network',
		baseURL: Urls.AUTH_API,
		method: 'POST',
		url: 'signout',
		errorKind: 'network',
		data: { token: 'synthetic' },
	},
	recovery500: {
		name: 'auth_recovery_500',
		baseURL: Urls.API,
		method: 'POST',
		url: 'passwordrecovery',
		errorKind: 'http_500',
		data: 'synthetic',
	},
	signup500: {
		name: 'auth_signup_500',
		baseURL: Urls.API,
		method: 'POST',
		url: 'user',
		errorKind: 'http_500',
		data: { email: 'synthetic', password: 'synthetic' },
	},
};

const checkoutDefinitions: Record<
	keyof Omit<SentryCheckoutTestScenarios, 'all'>,
	ScenarioDefinition
> = {
	openSellNetwork: {
		name: 'checkout_open_sell_network',
		baseURL: Urls.API,
		method: 'POST',
		url: 'sell/open',
		errorKind: 'network',
		data: { items: [] },
	},
	openSell500: {
		name: 'checkout_open_sell_500',
		baseURL: Urls.API,
		method: 'POST',
		url: 'sell/open',
		errorKind: 'http_500',
		data: { items: [] },
	},
	editSell500: {
		name: 'checkout_edit_sell_500',
		baseURL: Urls.API,
		method: 'PUT',
		url: 'sell/sentry-test/edit',
		errorKind: 'http_500',
		data: { items: [] },
	},
	coupon500: {
		name: 'checkout_coupon_500',
		baseURL: Urls.API,
		method: 'PUT',
		url: 'sell/sentry-test/discount/SENTRY_TEST',
		errorKind: 'http_500',
		data: {},
	},
	otp500: {
		name: 'checkout_otp_500',
		baseURL: Urls.API,
		method: 'POST',
		url: 'user/otp/validate',
		errorKind: 'http_500',
		data: { otp: 'synthetic', email: 'synthetic' },
	},
	addCard500: {
		name: 'checkout_add_card_500',
		baseURL: Urls.CHECKOUT_API,
		method: 'POST',
		url: 'wallet',
		errorKind: 'http_500',
		data: { cardNumber: 'synthetic', cardCvc: 'synthetic' },
	},
	validateCard500: {
		name: 'checkout_validate_card_500',
		baseURL: Urls.CHECKOUT_API,
		method: 'POST',
		url: 'wallet/sentry-test/validate',
		errorKind: 'http_500',
		data: { verificationCode: 'synthetic' },
	},
	buy500: {
		name: 'checkout_buy_500',
		baseURL: Urls.API,
		method: 'PUT',
		url: 'sell/sentry-test/buy',
		errorKind: 'http_500',
		data: {},
	},
	pay500: {
		name: 'checkout_pay_500',
		baseURL: Urls.API,
		method: 'PUT',
		url: 'sell/sentry-test/pay',
		errorKind: 'http_500',
		data: { paymentMethod: 'synthetic' },
	},
	pixStatus500: {
		name: 'checkout_pix_status_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'sell/sentry-test/status',
		errorKind: 'http_500',
	},
};

const eventCreationDefinitions: Record<
	keyof Omit<SentryEventCreationTestScenarios, 'all'>,
	ScenarioDefinition
> = {
	loadCategories500: {
		name: 'event_creation_load_categories_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'eventcategory',
		errorKind: 'http_500',
	},
	createDraftNetwork: {
		name: 'event_creation_create_draft_network',
		baseURL: Urls.EVENT_API,
		method: 'POST',
		url: '/',
		errorKind: 'network',
	},
	createDraft500: {
		name: 'event_creation_create_draft_500',
		baseURL: Urls.EVENT_API,
		method: 'POST',
		url: '/',
		errorKind: 'http_500',
	},
	loadDraft500: {
		name: 'event_creation_load_draft_500',
		baseURL: Urls.EVENT_API,
		method: 'GET',
		url: '/sentry-test-draft',
		errorKind: 'http_500',
	},
	createEditDraft500: {
		name: 'event_creation_create_edit_draft_500',
		baseURL: Urls.EVENT_API,
		method: 'POST',
		url: '/sentry-test-event/draft',
		errorKind: 'http_500',
	},
	loadSessions500: {
		name: 'event_creation_load_sessions_500',
		baseURL: Urls.EVENT_API,
		method: 'GET',
		url: '/sentry-test-draft/sessions',
		errorKind: 'http_500',
	},
	loadTickets500: {
		name: 'event_creation_load_tickets_500',
		baseURL: Urls.EVENT_API,
		method: 'GET',
		url: '/sentry-test-draft/tickets',
		errorKind: 'http_500',
	},
	createPlace500: {
		name: 'event_creation_create_place_500',
		baseURL: Urls.API,
		method: 'POST',
		url: 'place',
		errorKind: 'http_500',
		data: { name: 'synthetic' },
	},
	saveTicket500: {
		name: 'event_creation_save_ticket_500',
		baseURL: Urls.EVENT_API,
		method: 'POST',
		url: '/sentry-test-draft/ticket',
		errorKind: 'http_500',
		data: { synthetic: true },
	},
	sortTickets500: {
		name: 'event_creation_sort_tickets_500',
		baseURL: Urls.EVENT_API,
		method: 'PUT',
		url: '/sentry-test-draft/tickets/sort',
		errorKind: 'http_500',
		data: { tickets: [] },
	},
	uploadBanner500: {
		name: 'event_creation_upload_banner_500',
		baseURL: Urls.EVENT_API,
		method: 'PUT',
		url: '/sentry-test-draft/banner',
		errorKind: 'http_500',
		data: { synthetic: true },
	},
	updateTax500: {
		name: 'event_creation_update_tax_500',
		baseURL: Urls.EVENT_API,
		method: 'PUT',
		url: '/sentry-test-draft/tax',
		errorKind: 'http_500',
		data: { tax: 0.1 },
	},
	publish500: {
		name: 'event_creation_publish_500',
		baseURL: Urls.API,
		method: 'POST',
		url: '/event/publish',
		errorKind: 'http_500',
		data: { draftId: 'synthetic' },
	},
};

const publicEventDefinitions: Record<
	keyof Omit<SentryPublicEventTestScenarios, 'all'>,
	ScenarioDefinition
> = {
	loadEvent500: {
		name: 'public_event_load_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'eventinfo/sentry-test',
		errorKind: 'http_500',
	},
	loadTicketTypes500: {
		name: 'public_event_ticket_types_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'tickettype?eventId=sentry-test',
		errorKind: 'http_500',
	},
	openSell500: {
		name: 'public_event_sell_open_500',
		baseURL: Urls.API,
		method: 'POST',
		url: 'sell/open',
		errorKind: 'http_500',
		data: { items: [] },
	},
	contact500: {
		name: 'public_event_contact_500',
		baseURL: Urls.API,
		method: 'POST',
		url: 'event/sentry-test',
		errorKind: 'http_500',
		data: { message: 'synthetic' },
	},
	complaint500: {
		name: 'public_event_complaint_500',
		baseURL: Urls.API,
		method: 'POST',
		url: 'support/complaint?eventID=sentry-test',
		errorKind: 'http_500',
		data: { reason: 'synthetic', message: 'synthetic' },
	},
};

const eventManagementDefinitions: Record<
	keyof Omit<SentryEventManagementTestScenarios, 'all'>,
	ScenarioDefinition
> = {
	dashboard500: {
		name: 'event_management_dashboard_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'eventinfo/sentry-test',
		errorKind: 'http_500',
	},
	sales500: {
		name: 'event_management_sales_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'event/sentry-test/sales-origins',
		errorKind: 'http_500',
	},
	checkin500: {
		name: 'event_management_checkin_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'checkin/sentry-test',
		errorKind: 'http_500',
	},
	coupons500: {
		name: 'event_management_coupons_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'event/sentry-test/coupons',
		errorKind: 'http_500',
	},
	guests500: {
		name: 'event_management_guests_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'event/sentry-test/guests',
		errorKind: 'http_500',
	},
};

const payoutAccountDefinitions: Record<
	keyof Omit<SentryPayoutAccountTestScenarios, 'all'>,
	ScenarioDefinition
> = {
	validatePhone500: {
		name: 'payout_validate_phone_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'whatsapp/validate?phone=synthetic',
		errorKind: 'http_500',
		payoutStep: 'recipient_details',
	},
	loadBanks500: {
		name: 'payout_load_banks_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'banks',
		errorKind: 'http_500',
		payoutStep: 'bank_account',
	},
	saveRecipient500: {
		name: 'payout_save_recipient_500',
		baseURL: Urls.API,
		method: 'PUT',
		url: 'me/billingdata',
		errorKind: 'http_500',
		data: { document: 'synthetic' },
		payoutStep: 'review_submit',
	},
	registerBankAccountNetwork: {
		name: 'payout_register_bank_account_network',
		baseURL: Urls.API,
		method: 'PUT',
		url: 'me/billingdata/bank-account',
		errorKind: 'network',
		data: { accountNumber: 'synthetic' },
		payoutStep: 'review_submit',
	},
	registerBankAccount500: {
		name: 'payout_register_bank_account_500',
		baseURL: Urls.API,
		method: 'PUT',
		url: 'me/billingdata/bank-account',
		errorKind: 'http_500',
		data: { accountNumber: 'synthetic' },
		payoutStep: 'review_submit',
	},
	checkStatus500: {
		name: 'payout_check_status_500',
		baseURL: Urls.API,
		method: 'GET',
		url: 'me/billingdata/check?userId=sentry-test',
		errorKind: 'http_500',
		payoutStep: 'review_submit',
	},
};

const createErrorAdapter = (definition: ScenarioDefinition) => (config: any) => {
	const isNetworkError = definition.errorKind === 'network';
	const error = Object.assign(
		new Error(isNetworkError ? 'Network Error' : 'Request failed with status code 500'),
		{
			config,
			isAxiosError: true,
			request: {},
			response: isNetworkError
				? undefined
				: {
						status: 500,
						statusText: 'Internal Server Error',
						headers: {},
						config,
						data: {
							code: 'sentry_test_internal_error',
							message: 'Synthetic observability failure',
						},
					},
			toJSON: () => ({}),
		}
	);

	return Promise.reject(error);
};

const runScenario = async (
	definition: ScenarioDefinition
): Promise<ScenarioResult> => {
	const [{ default: axios }, apiUtils] = await Promise.all([
		import('axios'),
		import('src/api/utils'),
	]);
	const client = axios.create({ baseURL: definition.baseURL });

	client.interceptors.request.use(apiUtils.requestFullfilledCustomHeaders);
	client.interceptors.response.use(
		apiUtils.responseFullfilled,
		apiUtils.responseRejected
	);

	if (definition.payoutStep) {
		setSentryPayoutAccountState({
			isOpen: true,
			step: definition.payoutStep,
			surface: 'synthetic',
		});
	}

	try {
		await client
			.request({
				method: definition.method,
				url: definition.url,
				data: definition.data,
				adapter: createErrorAdapter(definition),
				observabilityTestScenario: definition.name,
			} as any)
			.catch(() => undefined);
	} finally {
		if (definition.payoutStep) resetSentryPayoutAccountState();
	}

	return {
		scenario: definition.name,
		expectedQuery: `environment:${Env} observability_test:true test_scenario:${definition.name}`,
	};
};

export const installSentryTestScenarios = () => {
	if (typeof window === 'undefined') return;

	const isLocalhost = ['localhost', '127.0.0.1'].includes(
		window.location.hostname
	);

	if (Env === 'production' || !isLocalhost) return;

	const authScenarios: Omit<SentryTestScenarios, 'all'> = {
		loginNetwork: () => runScenario(authDefinitions.loginNetwork),
		login500: () => runScenario(authDefinitions.login500),
		oauthNetwork: () => runScenario(authDefinitions.oauthNetwork),
		logoutNetwork: () => runScenario(authDefinitions.logoutNetwork),
		recovery500: () => runScenario(authDefinitions.recovery500),
		signup500: () => runScenario(authDefinitions.signup500),
	};
	const checkoutScenarios: Omit<SentryCheckoutTestScenarios, 'all'> = {
		openSellNetwork: () => runScenario(checkoutDefinitions.openSellNetwork),
		openSell500: () => runScenario(checkoutDefinitions.openSell500),
		editSell500: () => runScenario(checkoutDefinitions.editSell500),
		coupon500: () => runScenario(checkoutDefinitions.coupon500),
		otp500: () => runScenario(checkoutDefinitions.otp500),
		addCard500: () => runScenario(checkoutDefinitions.addCard500),
		validateCard500: () => runScenario(checkoutDefinitions.validateCard500),
		buy500: () => runScenario(checkoutDefinitions.buy500),
		pay500: () => runScenario(checkoutDefinitions.pay500),
		pixStatus500: () => runScenario(checkoutDefinitions.pixStatus500),
	};
	const eventCreationScenarios: Omit<
		SentryEventCreationTestScenarios,
		'all'
	> = {
		loadCategories500: () =>
			runScenario(eventCreationDefinitions.loadCategories500),
		createDraftNetwork: () =>
			runScenario(eventCreationDefinitions.createDraftNetwork),
		createDraft500: () =>
			runScenario(eventCreationDefinitions.createDraft500),
		loadDraft500: () => runScenario(eventCreationDefinitions.loadDraft500),
		createEditDraft500: () =>
			runScenario(eventCreationDefinitions.createEditDraft500),
		loadSessions500: () =>
			runScenario(eventCreationDefinitions.loadSessions500),
		loadTickets500: () =>
			runScenario(eventCreationDefinitions.loadTickets500),
		createPlace500: () =>
			runScenario(eventCreationDefinitions.createPlace500),
		saveTicket500: () =>
			runScenario(eventCreationDefinitions.saveTicket500),
		sortTickets500: () =>
			runScenario(eventCreationDefinitions.sortTickets500),
		uploadBanner500: () =>
			runScenario(eventCreationDefinitions.uploadBanner500),
		updateTax500: () =>
			runScenario(eventCreationDefinitions.updateTax500),
		publish500: () => runScenario(eventCreationDefinitions.publish500),
	};
	const payoutAccountScenarios: Omit<
		SentryPayoutAccountTestScenarios,
		'all'
	> = {
		validatePhone500: () =>
			runScenario(payoutAccountDefinitions.validatePhone500),
		loadBanks500: () => runScenario(payoutAccountDefinitions.loadBanks500),
		saveRecipient500: () =>
			runScenario(payoutAccountDefinitions.saveRecipient500),
		registerBankAccountNetwork: () =>
			runScenario(payoutAccountDefinitions.registerBankAccountNetwork),
		registerBankAccount500: () =>
			runScenario(payoutAccountDefinitions.registerBankAccount500),
		checkStatus500: () =>
			runScenario(payoutAccountDefinitions.checkStatus500),
	};
	const publicEventScenarios: Omit<SentryPublicEventTestScenarios, 'all'> = {
		loadEvent500: () => runScenario(publicEventDefinitions.loadEvent500),
		loadTicketTypes500: () =>
			runScenario(publicEventDefinitions.loadTicketTypes500),
		openSell500: () => runScenario(publicEventDefinitions.openSell500),
		contact500: () => runScenario(publicEventDefinitions.contact500),
		complaint500: () => runScenario(publicEventDefinitions.complaint500),
	};
	const eventManagementScenarios: Omit<
		SentryEventManagementTestScenarios,
		'all'
	> = {
		dashboard500: () =>
			runScenario(eventManagementDefinitions.dashboard500),
		sales500: () => runScenario(eventManagementDefinitions.sales500),
		checkin500: () => runScenario(eventManagementDefinitions.checkin500),
		coupons500: () => runScenario(eventManagementDefinitions.coupons500),
		guests500: () => runScenario(eventManagementDefinitions.guests500),
	};

	window.__SENTRY_AUTH_TESTS__ = {
		...authScenarios,
		all: () => Promise.all(Object.values(authScenarios).map(run => run())),
	};
	window.__SENTRY_CHECKOUT_TESTS__ = {
		...checkoutScenarios,
		all: () => Promise.all(Object.values(checkoutScenarios).map(run => run())),
	};
	window.__SENTRY_EVENT_CREATION_TESTS__ = {
		...eventCreationScenarios,
		all: () =>
			Promise.all(Object.values(eventCreationScenarios).map(run => run())),
	};
	window.__SENTRY_PAYOUT_ACCOUNT_TESTS__ = {
		...payoutAccountScenarios,
		all: async () => {
			const results: ScenarioResult[] = [];
			for (const run of Object.values(payoutAccountScenarios)) {
				results.push(await run());
			}
			return results;
		},
	};
	window.__SENTRY_PUBLIC_EVENT_TESTS__ = {
		...publicEventScenarios,
		all: () =>
			Promise.all(Object.values(publicEventScenarios).map(run => run())),
	};
	window.__SENTRY_EVENT_MANAGEMENT_TESTS__ = {
		...eventManagementScenarios,
		all: () =>
			Promise.all(Object.values(eventManagementScenarios).map(run => run())),
	};

	const scenarios = {
		...authScenarios,
		...checkoutScenarios,
		...eventCreationScenarios,
		...payoutAccountScenarios,
		publicEventLoadEvent500: publicEventScenarios.loadEvent500,
		publicEventLoadTicketTypes500: publicEventScenarios.loadTicketTypes500,
		publicEventOpenSell500: publicEventScenarios.openSell500,
		publicEventContact500: publicEventScenarios.contact500,
		publicEventComplaint500: publicEventScenarios.complaint500,
		eventManagementDashboard500: eventManagementScenarios.dashboard500,
		eventManagementSales500: eventManagementScenarios.sales500,
		eventManagementCheckin500: eventManagementScenarios.checkin500,
		eventManagementCoupons500: eventManagementScenarios.coupons500,
		eventManagementGuests500: eventManagementScenarios.guests500,
	};

	const searchParams = new URLSearchParams(window.location.search);
	const requestedScenario = searchParams.get(
		'sentry_test'
	) as keyof typeof scenarios | null;

	if (requestedScenario && requestedScenario in scenarios) {
		searchParams.delete('sentry_test');
		const nextSearch = searchParams.toString();
		window.history.replaceState(
			window.history.state,
			'',
			`${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${
				window.location.hash
			}`
		);

		Promise.resolve()
			.then(() => scenarios[requestedScenario]())
			.catch(error => {
				console.warn('[sentry-test] Failed to run scenario.', error);
			});
	}
};
